import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Flex,
  Box,
  Text,
  FormControl,
  Textarea,
  Select,
  useColorModeValue,
} from '@chakra-ui/react';
import InvoiceProductSidebar from './InvoiceProductSidebar';
import SelectedInvoiceProductsTable from './SelectedInvoiceProductsTable';
import InvoiceCustomerSelector from './InvoiceCustomerSelector';
import InvoiceSupplierSelector from './InvoiceSupplierSelector';
import ProductSelectionModal from './ProductSelectionModal';
import api from '../api';

const InvoiceModal = ({ isOpen, onClose, type, invoiceData, onInvoiceCreated }) => {
  // Color mode values for modern styling (consistent with other pages)
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const subtleTextColor = useColorModeValue('gray.600', 'gray.400');
  const shadowColor = useColorModeValue('rgba(0, 0, 0, 0.1)', 'rgba(0, 0, 0, 0.3)');
  const filterBg = useColorModeValue('white', 'gray.800');
  
  const [formData, setFormData] = useState({
    customer: '',
    supplier: '',
    note: '',
    entries: [],
    total_without_vat: 0,
    vat: 0,
    total_with_vat: 0,
    status: 'new',
  });
  
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [filters, setFilters] = useState({ name: '' });
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);

  useEffect(() => {
    if (invoiceData) {
      setFormData({
        ...invoiceData,
        customer: invoiceData.customer ? invoiceData.customer : undefined,
        supplier: invoiceData.supplier ? invoiceData.supplier : undefined,
        entries: invoiceData.entries.map((entry) => ({
          ...entry,
          product_id: entry.product?.id || entry.product_id, 
          name: entry.product?.name || entry.name,
        })),
      });
    } else {
      setFormData({ customer: undefined, supplier: undefined, note: '', entries: [] }); 
    }
  }, [invoiceData]);

  const fetchProducts = async (page = 1) => {
    setLoadingProducts(true);
    try {
      const response = await api.get(
        `/products/?page=${page}&search=${filters.name}&categories=${selectedCategories.join(',')}`
      );
      const newProducts = response.data.results;
      setProducts((prev) => (page === 1 ? newProducts : [...prev, ...newProducts]));
      setHasMore(!!response.data.next);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoadingProducts(false);
    }
  };

  useEffect(() => {
    console.log('formData.customer updated:', formData.customer);
  }, [formData.customer]);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories/hierarchy/');
      setCategories(response.data); // Hierarchical categories
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers/?all=true');
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await api.get('/suppliers/?all=true');
      setSuppliers(response.data);
    } catch (error) {
      console.error('Failed to fetch suppliers:', error);
    }
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.get('/categories/hierarchy/');
        setCategories(response.data); // Store hierarchical categories
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };

    const fetchSuppliers = async () => {
      try {
        const response = await api.get('/suppliers/?all=true');
        setSuppliers(response.data);
      } catch (error) {
        console.error('Failed to fetch suppliers:', error);
      }
    };
  
    const fetchCustomers = async () => {
      try {
        const response = await api.get('/customers/?all=true');
        setCustomers(response.data);
      } catch (error) {
        console.error('Failed to fetch customers:', error);
      }
    };
    
    fetchSuppliers()
    fetchCustomers();
    fetchCategories(); // Call fetchCategories here
  }, []);
  

  const handleAddProduct = (product) => {
    if (!formData.entries.some((entry) => entry.product_id === product.id)) {
      const markup = type === 'arrival' ? 0 : product.markup_percentage;
      setFormData((prev) => ({
        ...prev,
        entries: [
          ...prev.entries,
          {
            product_id: product.id,
            quantity: 1,
            price: product.price,
            purchase_price: product.purchase_price,
            markup_percentage: markup,
            name: product.name,
          },
        ],
      }));
    }
  };

  const handleProductChange = (productId, field, value) => {
    setFormData((prev) => ({
      ...prev,
      entries: prev.entries.map((entry) =>
        entry.product_id === productId ? { ...entry, [field]: value } : entry
      ),
    }));
  };

  const handleRemoveEntry = (productId) => {
    console.log('Removing product with ID:', productId);
    setFormData((prev) => ({
      ...prev,
      entries: prev.entries.filter((entry) => entry.product_id !== productId),
    }));
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        ...formData,
        ...(formData.customer ? { customer: formData.customer } : {}),
        entries: formData.entries.map((entry) => ({
          product_id: entry.product_id,
          quantity: entry.quantity,
          price: entry.price,
          purchase_price: entry.purchase_price,
          markup_percentage: entry.markup_percentage,
        })),
        total_without_vat: totals.totalWithoutVat,
        vat: totals.vat,
        total_with_vat: totals.totalWithVat,
      };
  
      let response;
      if (invoiceData) {
        // Edit existing invoice
        response = await api.put(`/${type}-invoices/${invoiceData.id}/`, payload);
        if (onInvoiceCreated) {
          onInvoiceCreated(response.data, true); // Pass true to indicate this is an update
        }
      } else {
        // Create new invoice
        response = await api.post(`/${type}-invoices/`, payload);
        if (onInvoiceCreated) {
          onInvoiceCreated(response.data, false); // Pass false to indicate this is a creation
        }
      }
    
      onClose();
    } catch (error) {
      console.error('Failed to save invoice:', error.response?.data || error.message); // Log backend error
    }
  };

  const handleCloseModal = () => {
    setFormData({ customer: '', note: '', entries: [] });
    setSelectedCategories([]);
    setFilters({ name: '' });
    setProducts([]);
    onClose();
  };

  // Calculate totals
  const calculateTotals = () => {
    const totalWithoutVat = formData.entries.reduce((sum, entry) => {
      const quantity = parseFloat(entry.quantity) || 0;
      const price = type === 'arrival'
        ? parseFloat(entry.purchase_price) || 0
        : parseFloat(entry.price) || 0;
      return sum + quantity * price;
    }, 0);

    const vat = 0;
    const totalWithVat = totalWithoutVat + vat;
  
    return {
      totalWithoutVat: Number(totalWithoutVat.toFixed(2)) || 0,
      vat: Number(vat.toFixed(2)) || 0,
      totalWithVat: Number(totalWithVat.toFixed(2)) || 0,
    };
  };
  
  
  
  const totals = calculateTotals();
  

  return (
    <Modal isOpen={isOpen} onClose={handleCloseModal} isCentered size={{ base: 'full', md: '6xl' }}>
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(10px)" />
      <ModalContent 
        maxWidth={{ base: '100%', md: '98%' }} 
        h={{ base: '100vh', md: '95vh' }} 
        bg={cardBg}
        borderRadius={{ base: 0, md: '2xl' }}
        boxShadow={`0 20px 25px -5px ${shadowColor}, 0 10px 10px -5px ${shadowColor}`}
        border="1px solid"
        borderColor={borderColor}
        position="relative"
        overflow="hidden"
        display="flex"
        flexDirection="column"
      >
        {/* Top accent bar */}
        <Box position="absolute" top={0} left={0} right={0} height="6px" bg="linear-gradient(90deg, #667eea, #764ba2, #f093fb, #f5576c)" />
        <ModalHeader color={textColor} py={{ base: 3, md: 6 }} px={{ base: 4, md: 8 }} fontSize={{ base: 'lg', md: '2xl' }} fontWeight="bold" bgGradient="linear(to-r, #667eea, #764ba2)" bgClip="text" mt={{ base: 1, md: 2 }}>
          {invoiceData ? '✏️ Редагувати накладну' : '📄 Створити накладну'}
        </ModalHeader>
        <ModalCloseButton color={textColor} size={{ base: 'md', md: 'lg' }} top={{ base: 3, md: 6 }} right={{ base: 4, md: 8 }} />
        <ModalBody display="flex" flexDirection="column" h={{ base: 'calc(100% - 120px)', md: 'calc(100% - 140px)' }} p={0} overflow="hidden">
          <Flex flex="1" overflow="hidden" direction={{ base: 'column', md: 'row' }}>
            {/* Sidebar Card */}
            <Box
              flex={{ base: '0 0 auto', md: '0 0 340px' }}
              maxH={{ base: '35vh', md: '100%' }}
              p={{ base: 3, md: 4 }}
              overflowY="auto"
              h="100%"
              display={{ base: 'none', md: 'block' }}
            >
              <Box bg={cardBg} borderRadius="xl" border="1px solid" borderColor={borderColor} p={{ base: 3, md: 4 }} boxShadow={`0 10px 15px -3px ${shadowColor}`} position="relative" overflow="hidden" h="100%">
                <Box position="absolute" top={0} left={0} right={0} height="4px" bg="linear-gradient(90deg, #4facfe, #00f2fe)" />
                <InvoiceProductSidebar
                  filters={filters}
                  setFilters={setFilters}
                  categories={categories}
                  selectedCategories={selectedCategories}
                  setSelectedCategories={setSelectedCategories}
                  handleAddProduct={handleAddProduct}
                  handleRemoveProduct={handleRemoveEntry}
                  selectedProducts={formData.entries.map((entry) => entry.product_id)}
                />
              </Box>
            </Box>
            {/* Main Content Card */}
            <Box flex="1" p={{ base: 3, md: 4 }} h="100%" display="flex" flexDirection="column">
              <Box bg={cardBg} borderRadius="xl" border="1px solid" borderColor={borderColor} boxShadow={`0 10px 15px -3px ${shadowColor}`} h="100%" display="flex" flexDirection="column" overflow="hidden" position="relative">
                <Box position="absolute" top={0} left={0} right={0} height="4px" bg="linear-gradient(90deg, #f093fb, #f5576c)" />
                {/* Products Table Section */}
                <Box flex="2" p={{ base: 3, md: 4 }} overflowY="auto" mt={{ base: 2, md: 4 }}>
                  <Flex justify="space-between" align="center" mb={3} wrap="wrap" gap={2}>
                    <Text fontSize={{ base: 'md', md: 'lg' }} fontWeight="bold" color={textColor} bgGradient="linear(to-r, #f093fb, #f5576c)" bgClip="text">📦 Товари в накладній</Text>
                    <Button size={{ base: 'sm', md: 'sm' }} bg="linear-gradient(135deg, #4facfe, #00f2fe)" color="white" leftIcon={<Text fontSize="md">🛍️</Text>} onClick={() => setIsProductModalOpen(true)} borderRadius="lg" px={{ base: 3, md: 4 }} boxShadow="0 4px 6px rgba(79, 172, 254, 0.3)" _hover={{ bg: 'linear-gradient(135deg, #3b9cfe, #00d9fe)', transform: { base: 'none', md: 'translateY(-1px)' }, boxShadow: '0 6px 15px rgba(79, 172, 254, 0.4)' }} transition="all 0.2s">Додати товари</Button>
                  </Flex>
                  <Box overflowX={{ base: 'auto', md: 'visible' }} w="100%" pr={{ base: 2, md: 0 }}>
                    <Box minW={{ base: '760px', md: 'auto' }}>
                      <SelectedInvoiceProductsTable type={type} entries={formData.entries} products={products} handleProductChange={handleProductChange} handleRemoveEntry={handleRemoveEntry} />
                    </Box>
                  </Box>
                </Box>
                {/* Bottom Section */}
                <Box 
                  flex="0 0 auto"
                  borderTop="1px solid"
                  borderColor={borderColor}
                  p={{ base: 3, md: 4 }}
                  bg={useColorModeValue('gray.50', 'gray.700')}
                  maxH={{ base: '40vh', md: 'unset' }}
                  overflowY={{ base: 'auto', md: 'visible' }}
                >
                  <Flex gap={{ base: 3, md: 4 }} align="flex-start" direction={{ base: 'column', md: 'row' }}>
                    {/* Left side - Customer/Supplier */}
                    <Box flex={{ base: '1', md: '1' }} w="full">
                      <Text fontSize="md" fontWeight="600" color={textColor} mb={3} bgGradient="linear(to-r, #667eea, #764ba2)" bgClip="text">📋 {type === 'arrival' ? 'Постачальник' : 'Клієнт'}</Text>
                      {type === 'arrival' ? (
                        <InvoiceSupplierSelector suppliers={suppliers} selectedSupplier={formData.supplier} setSelectedSupplier={(id) => setFormData((prev) => ({ ...prev, supplier: id }))} fetchSuppliers={fetchSuppliers} />
                      ) : (
                        <InvoiceCustomerSelector customers={customers} selectedCustomer={formData.customer} setSelectedCustomer={(id) => setFormData((prev) => ({ ...prev, customer: id }))} fetchCustomers={fetchCustomers} />
                      )}
                    </Box>

                    {/* Middle - Notes */}
                    <Box flex={{ base: '1', md: '1' }} w="full">
                      <Text fontSize="md" fontWeight="600" color={textColor} mb={3} bgGradient="linear(to-r, #4facfe, #00f2fe)" bgClip="text">💬 Примітка</Text>
                      <Textarea
                        placeholder="Додайте примітку..."
                        value={formData.note}
                        onChange={(e) => setFormData((prev) => ({ ...prev, note: e.target.value }))}
                        bg={cardBg}
                        border="1px solid"
                        borderColor={borderColor}
                        borderRadius="lg"
                        size="sm"
                        minH={{ base: '120px', md: '80px' }}
                        _focus={{ borderColor: '#4facfe', boxShadow: '0 0 0 1px rgba(79, 172, 254, 0.3)' }}
                        _placeholder={{ color: subtleTextColor }}
                      />
                    </Box>

                    {/* Right side - Totals */}
                    <Box flex={{ base: '1', md: '0 0 280px' }} w="full">
                      <Text fontSize="md" fontWeight="600" color={textColor} mb={3} bgGradient="linear(to-r, #667eea, #764ba2)" bgClip="text">💰 Підсумок</Text>
                      <Box bg={filterBg} borderRadius="lg" border="1px solid" borderColor={borderColor} p={3} boxShadow={`0 4px 6px -1px ${shadowColor}`} position="relative" overflow="hidden">
                        <Box position="absolute" top={0} left={0} right={0} height="3px" bg="linear-gradient(90deg, #667eea, #764ba2)" />
                        <Flex justify="space-between" mb={1} mt={1}><Text fontSize="sm" color={subtleTextColor}>Без ПДВ:</Text><Text fontSize="sm" fontWeight="500" color={textColor}>{(totals?.totalWithoutVat ? parseFloat(totals.totalWithoutVat) : 0).toFixed(2)} грн</Text></Flex>
                        <Flex justify="space-between" mb={2}><Text fontSize="sm" color={subtleTextColor}>ПДВ:</Text><Text fontSize="sm" fontWeight="500" color={textColor}>0.00 грн</Text></Flex>
                        <Flex justify="space-between" pt={2} borderTop="1px solid" borderColor={borderColor}><Text fontSize="sm" fontWeight="600" color={textColor}>Всього:</Text><Text fontWeight="700" fontSize="md" bgGradient="linear(to-r, #667eea, #764ba2)" bgClip="text">{(totals?.totalWithoutVat ? parseFloat(totals.totalWithoutVat) : 0).toFixed(2)} грн</Text></Flex>
                      </Box>
                    </Box>
                  </Flex>
                </Box>
              </Box>
            </Box>
          </Flex>
        </ModalBody>
        <ModalFooter borderTop="1px solid" borderColor={borderColor} py={{ base: 3, md: 4 }} px={{ base: 4, md: 8 }} bg={useColorModeValue('gray.50', 'gray.700')} flexShrink={0} position="relative" zIndex={10} minH="56px">
          <Button variant="ghost" onClick={onClose} mr={{ base: 2, md: 3 }} borderRadius="lg" size={{ base: 'sm', md: 'md' }} px={{ base: 4, md: 6 }} color={textColor} _hover={{ bg: useColorModeValue('gray.100', 'gray.600') }}>
            ❌ Скасувати
          </Button>
          <Button bg="linear-gradient(135deg, #667eea, #764ba2)" color="white" onClick={handleSubmit} borderRadius="lg" size={{ base: 'sm', md: 'md' }} px={{ base: 6, md: 8 }} boxShadow="0 4px 15px rgba(102, 126, 234, 0.4)" _hover={{ bg: 'linear-gradient(135deg, #5a6fd8, #6a4190)', transform: { base: 'none', md: 'translateY(-2px)' }, boxShadow: '0 8px 25px rgba(102, 126, 234, 0.6)' }} _active={{ transform: 'translateY(0)' }} transition="all 0.2s">
            💾 Зберегти
          </Button>
        </ModalFooter>
      </ModalContent>
      {/* Product Selection Modal unchanged */}
      <ProductSelectionModal isOpen={isProductModalOpen} onClose={() => setIsProductModalOpen(false)} onAddProduct={handleAddProduct} onRemoveProduct={handleRemoveEntry} selectedProducts={formData.entries.map((entry) => entry.product_id)} categories={categories} />
    </Modal>
  );
};

export default InvoiceModal;
